/**
 * Copyright 2023-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button, Input, Label, theme } from "@nordcloud/gnui";
import { ChannelType } from "~/generated/graphql";
import { FormGroup, Sidebar } from "~/components";
import { useDisclosure } from "~/hooks";
import { useUpdateNotificationGroup } from "~/views/notificationGroups/hooks/useUpdateNotificationGroup/useUpdateNotificationGroup";
import { useNotificationGroup } from "~/views/notificationGroups/notificationGroupDetails/NotificationGroupProvider";
import { emailSchema } from "~/views/notificationGroups/notificationGroupDetails/schema";

export function AddEmailForm() {
  const { data } = useNotificationGroup();
  const { isOpen, open, close } = useDisclosure();

  const onSubmit = (emailData: { email: string }) => {
    updateNotificationGroup({
      id: data?.id,
      channels: [
        {
          type: ChannelType.Email,
          typeProperties: { email: emailData },
        },
      ],
    });
  };

  const [updateNotificationGroup] = useUpdateNotificationGroup({
    onSuccess: () => {
      close();
      reset();
    },
  });

  const { handleSubmit, formState, register, reset } = useForm({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      id: data?.id,
      email: "",
    },
  });

  const error = formState.errors;

  return (
    <>
      <Button icon="plus" severity="low" onClick={open}>
        Add Email
      </Button>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Sidebar
          isOpen={isOpen}
          title="Add Email"
          onClick={close}
          footer={
            <div
              css={{
                margin: `${theme.spacing.spacing04} ${theme.spacing.spacing01}`,
              }}
            >
              <Button
                icon="checkmark"
                ml={theme.spacing.spacing04}
                type="submit"
              >
                Apply
              </Button>
            </div>
          }
        >
          <FormGroup error={error.email}>
            <Label name="Email" htmlFor="email" />
            <Input
              type="text"
              aria-label="provide email"
              placeholder="Email"
              id="email"
              {...register("email")}
            />
          </FormGroup>
        </Sidebar>
      </form>
    </>
  );
}
